import _ from 'lodash'
import { TransformData, UnitSize, Z_LAYER_KEYS, Z_LAYER_VALUE_MAP } from '@wix/thunderbolt-becky-types'
import {
	getRotateString,
	getScaleString,
	getSkewString,
	getTranslateString,
	isStyleItemContainsTransformProps,
} from '@wix/thunderbolt-commons'

const orderedTransformationKeys = ['translate', 'scale', 'rotate', 'skew']

const isPropertyDefined = (property: keyof TransformData, styleItem: Partial<TransformData>) =>
	styleItem.hasOwnProperty(property) && styleItem[property] !== undefined && styleItem[property] !== null

const omitUndefinedProperties = (styleItem: TransformData): Partial<TransformData> =>
	_.pickBy(styleItem, (__, property) => isPropertyDefined(property as keyof TransformData, styleItem))

const omitIgnoredProperties = (
	styleItem: Partial<TransformData>,
	ignoredProperties: Array<string>
): Partial<TransformData> => _.omitBy(styleItem, (__, property) => ignoredProperties.includes(property))

const getUnit = (unit: UnitSize['type']) => {
	if (unit === 'percentage') {
		return '%'
	}
	return unit
}

const resolveValueWithUnit = (valueWithUnit?: UnitSize) =>
	valueWithUnit ? `${valueWithUnit.value}${getUnit(valueWithUnit.type)}` : 0

const getCSSObjectFromTransformationItem = (
	styleItem: TransformData,
	ignoredTransformProps: Array<string>,
	componentDefaultRotation?: number
) => {
	const css: { [cssProperty: string]: string } = {}
	const filteredStyleItem = omitIgnoredProperties(omitUndefinedProperties(styleItem), ignoredTransformProps)
	if (Z_LAYER_VALUE_MAP[filteredStyleItem.zLayer as Z_LAYER_KEYS]) {
		css['z-index'] = `${Z_LAYER_VALUE_MAP[filteredStyleItem.zLayer as Z_LAYER_KEYS]}`
	}
	if (filteredStyleItem.hasOwnProperty('hidden')) {
		css.opacity = `${filteredStyleItem.hidden ? 0 : 1}`
		css.visibility = `${filteredStyleItem.hidden ? 'hidden' : 'visible'}`
	} else if (filteredStyleItem.hasOwnProperty('opacity')) {
		css.opacity = `${filteredStyleItem.opacity}`
		css['--comp-opacity'] = `${filteredStyleItem.opacity}`
	}
	if (filteredStyleItem.origin) {
		css['transform-origin'] = `${resolveValueWithUnit(filteredStyleItem.origin.x)} ${resolveValueWithUnit(
			filteredStyleItem.origin.y
		)}`
	}
	if (filteredStyleItem.filter) {
		const { blur = 0, opacity = 100, grayscale = 0 } = filteredStyleItem.filter
		css.filter = `opacity(${opacity}%) grayscale(${grayscale}%) blur(${blur}px)`
	}
	if (isStyleItemContainsTransformProps(filteredStyleItem)) {
		const { scale, rotate, translate, skew } = filteredStyleItem

		const transformationsString = {
			translate: getTranslateString(translate),
			scale: getScaleString(scale),
			rotate: getRotateString(componentDefaultRotation, rotate),
			skew: getSkewString(skew),
		}

		const transformStringValue = orderedTransformationKeys
			.reduce(
				(transformString, transformKey) =>
					`${transformString}${
						transformationsString[transformKey as 'translate' | 'scale' | 'rotate' | 'skew']
					}`,
				''
			)
			.trim()
		if (transformStringValue) {
			css.transform = transformStringValue
			css['--comp-rotate-z'] = `${(componentDefaultRotation || 0) + (rotate || 0)}deg`
		}
	}

	return css
}

export { getCSSObjectFromTransformationItem }
